import { applyAction } from "../actions/apply-action";
import { createMatch } from "../transitions/create-match";
import type {
  ActionValidationFailure,
  AnyReplayDefinition,
  EngineContext,
  GameEvent,
  GameState,
  GameStateV3,
  GameStateV4,
  GameStateV5,
  ReplayDefinition,
  ReplayDefinitionV2,
  ReplayDefinitionV3,
  ReplayDefinitionV4,
  ReplayDefinitionV5,
  ReplayResult,
  ReplayV2Result,
  ReplayV3Result,
  ReplayV4Result,
  ReplayV5Result,
  SeededRandomState,
  SetupGameState,
} from "../types";
import { getStateHash } from "./state-hash";

function replayVersionOne(replay: ReplayDefinition, context: EngineContext): ReplayResult {
  let state: GameState = replay.initialState;
  const events: GameEvent[] = [];
  const stateHashes: string[] = [getStateHash(state)];

  if (
    replay.rulesVersion !== context.rulesVersion ||
    replay.cardDataVersion !== context.cardDataVersion
  ) {
    return {
      ok: false,
      state,
      events,
      stateHashes,
      failedActionIndex: -1,
      failure: {
        code: "CONTEXT_VERSION_MISMATCH",
        message: "Replay versions must match the supplied engine context.",
        path: "rulesVersion",
        details: {
          replayRulesVersion: replay.rulesVersion,
          contextRulesVersion: context.rulesVersion,
          replayCardDataVersion: replay.cardDataVersion,
          contextCardDataVersion: context.cardDataVersion,
        },
      },
    };
  }

  for (let index = 0; index < replay.actions.length; index += 1) {
    const action = replay.actions[index];
    if (action === undefined) {
      throw new Error("Replay action index unexpectedly missing.");
    }

    const result = applyAction(state, action, context);
    if (!result.ok) {
      return {
        ok: false,
        state,
        events,
        stateHashes,
        failedActionIndex: index,
        failure: result.failure,
      };
    }

    state = result.state;
    events.push(...result.events);
    stateHashes.push(getStateHash(state));
  }

  return { ok: true, state, events, stateHashes };
}

function randomStatesEqual(left: SeededRandomState, right: SeededRandomState): boolean {
  return (
    left.algorithm === right.algorithm &&
    left.seed === right.seed &&
    left.value === right.value &&
    left.draws === right.draws
  );
}

function setupMismatchFailure(
  replay: Pick<
    ReplayDefinitionV2 | ReplayDefinitionV3 | ReplayDefinitionV4 | ReplayDefinitionV5,
    "setupVerification"
  >,
  state: Readonly<SetupGameState>,
  initialStateHash: string,
): ActionValidationFailure | null {
  const expected = replay.setupVerification;
  if (
    expected.gameplayCatalogHash === state.setup.gameplayCatalogHash &&
    expected.initialStateHash === initialStateHash &&
    randomStatesEqual(expected.initialRandomState, state.setup.initialRandomState) &&
    randomStatesEqual(expected.finalRandomState, state.setup.finalSetupRandomState)
  ) {
    return null;
  }

  return {
    code: "REPLAY_SETUP_MISMATCH",
    message: "Replay setup verification does not match the deterministically recreated match.",
    path: "setupVerification",
    details: {
      expectedCatalogHash: expected.gameplayCatalogHash,
      actualCatalogHash: state.setup.gameplayCatalogHash,
      expectedInitialStateHash: expected.initialStateHash,
      actualInitialStateHash: initialStateHash,
      expectedFinalRandomDraws: expected.finalRandomState.draws,
      actualFinalRandomDraws: state.setup.finalSetupRandomState.draws,
    },
  };
}

function replayVersionTwo(replay: ReplayDefinitionV2, context: EngineContext): ReplayV2Result {
  if (
    replay.rulesVersion !== context.rulesVersion ||
    replay.cardDataVersion !== context.cardDataVersion
  ) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "CONTEXT_VERSION_MISMATCH",
        message: "Replay versions must match the supplied engine context.",
        path: "rulesVersion",
        details: {
          replayRulesVersion: replay.rulesVersion,
          contextRulesVersion: context.rulesVersion,
          replayCardDataVersion: replay.cardDataVersion,
          contextCardDataVersion: context.cardDataVersion,
        },
      },
    };
  }

  if (
    replay.setup.rulesVersion !== replay.rulesVersion ||
    replay.setup.cardDataVersion !== replay.cardDataVersion ||
    replay.setup.catalog.schemaVersion !== 1
  ) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "REPLAY_SETUP_MISMATCH",
        message: "Replay V2 setup versions must match the replay header.",
        path: "setup",
        details: {
          replayRulesVersion: replay.rulesVersion,
          setupRulesVersion: replay.setup.rulesVersion,
          replayCardDataVersion: replay.cardDataVersion,
          setupCardDataVersion: replay.setup.cardDataVersion,
          setupCatalogSchemaVersion: replay.setup.catalog.schemaVersion,
        },
      },
    };
  }

  const setup = createMatch(replay.setup);
  if (!setup.ok) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: setup.failure,
    };
  }

  if (setup.state.schemaVersion !== 2) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "REPLAY_SETUP_MISMATCH",
        message: "Replay V2 must recreate a schema-V2 game state.",
        path: "setup.catalog.schemaVersion",
        details: { actualStateSchemaVersion: setup.state.schemaVersion },
      },
    };
  }

  let state = setup.state;
  const events: GameEvent[] = [...setup.events];
  const stateHashes = [setup.initialStateHash];
  const mismatch = setupMismatchFailure(replay, state, setup.initialStateHash);
  if (mismatch !== null) {
    return {
      ok: false,
      state,
      events,
      stateHashes,
      failedActionIndex: -1,
      failure: mismatch,
    };
  }

  for (let index = 0; index < replay.actions.length; index += 1) {
    const action = replay.actions[index];
    if (action === undefined) {
      throw new Error("Replay action index unexpectedly missing.");
    }

    const result = applyAction(state, action, context);
    if (!result.ok) {
      return {
        ok: false,
        state,
        events,
        stateHashes,
        failedActionIndex: index,
        failure: result.failure,
      };
    }
    if (result.state.schemaVersion !== 2) {
      throw new Error("Replay V2 action unexpectedly changed the game-state schema version.");
    }

    state = result.state;
    events.push(...result.events);
    stateHashes.push(getStateHash(state));
  }

  return { ok: true, state, events, stateHashes };
}

function replayVersionThree(replay: ReplayDefinitionV3, context: EngineContext): ReplayV3Result {
  if (
    replay.rulesVersion !== context.rulesVersion ||
    replay.cardDataVersion !== context.cardDataVersion
  ) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "CONTEXT_VERSION_MISMATCH",
        message: "Replay versions must match the supplied engine context.",
        path: "rulesVersion",
        details: {
          replayRulesVersion: replay.rulesVersion,
          contextRulesVersion: context.rulesVersion,
          replayCardDataVersion: replay.cardDataVersion,
          contextCardDataVersion: context.cardDataVersion,
        },
      },
    };
  }

  if (
    replay.setup.rulesVersion !== replay.rulesVersion ||
    replay.setup.cardDataVersion !== replay.cardDataVersion ||
    (replay.setup.catalog.schemaVersion !== 2 && replay.setup.catalog.schemaVersion !== 3)
  ) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "REPLAY_SETUP_MISMATCH",
        message: "Replay V3 setup versions and catalog schema 2 or 3 must match the replay header.",
        path: "setup",
        details: {
          replayRulesVersion: replay.rulesVersion,
          setupRulesVersion: replay.setup.rulesVersion,
          replayCardDataVersion: replay.cardDataVersion,
          setupCardDataVersion: replay.setup.cardDataVersion,
          setupCatalogSchemaVersion: replay.setup.catalog.schemaVersion,
        },
      },
    };
  }

  const setup = createMatch(replay.setup);
  if (!setup.ok) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: setup.failure,
    };
  }
  if (setup.state.schemaVersion !== 3) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "REPLAY_SETUP_MISMATCH",
        message: "Replay V3 must recreate a schema-V3 game state.",
        path: "setup.catalog.schemaVersion",
        details: { actualStateSchemaVersion: setup.state.schemaVersion },
      },
    };
  }

  let state: GameStateV3 = setup.state;
  const events: GameEvent[] = [...setup.events];
  const stateHashes = [setup.initialStateHash];
  const mismatch = setupMismatchFailure(replay, state, setup.initialStateHash);
  if (mismatch !== null) {
    return {
      ok: false,
      state,
      events,
      stateHashes,
      failedActionIndex: -1,
      failure: mismatch,
    };
  }

  for (let index = 0; index < replay.actions.length; index += 1) {
    const action = replay.actions[index];
    if (action === undefined) throw new Error("Replay action index unexpectedly missing.");
    const result = applyAction(state, action, context);
    if (!result.ok) {
      return {
        ok: false,
        state,
        events,
        stateHashes,
        failedActionIndex: index,
        failure: result.failure,
      };
    }
    if (result.state.schemaVersion !== 3) {
      throw new Error("Replay V3 action unexpectedly changed the game-state schema version.");
    }
    state = result.state;
    events.push(...result.events);
    stateHashes.push(getStateHash(state));
  }

  return { ok: true, state, events, stateHashes };
}

function replayVersionFour(replay: ReplayDefinitionV4, context: EngineContext): ReplayV4Result {
  if (
    replay.rulesVersion !== context.rulesVersion ||
    replay.cardDataVersion !== context.cardDataVersion
  ) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "CONTEXT_VERSION_MISMATCH",
        message: "Replay versions must match the supplied engine context.",
        path: "rulesVersion",
        details: {
          replayRulesVersion: replay.rulesVersion,
          contextRulesVersion: context.rulesVersion,
          replayCardDataVersion: replay.cardDataVersion,
          contextCardDataVersion: context.cardDataVersion,
        },
      },
    };
  }
  if (
    replay.setup.rulesVersion !== replay.rulesVersion ||
    replay.setup.cardDataVersion !== replay.cardDataVersion ||
    (replay.setup.catalog.schemaVersion !== 2 && replay.setup.catalog.schemaVersion !== 3)
  ) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "REPLAY_SETUP_MISMATCH",
        message: "Replay V4 setup versions and catalog schema 2 or 3 must match the replay header.",
        path: "setup",
        details: { setupCatalogSchemaVersion: replay.setup.catalog.schemaVersion },
      },
    };
  }
  const setup = createMatch(replay.setup);
  if (!setup.ok || setup.state.schemaVersion !== 4) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: !setup.ok
        ? setup.failure
        : {
            code: "REPLAY_SETUP_MISMATCH",
            message: "Replay V4 must recreate a schema-V4 game state.",
            path: "setup",
            details: { actualStateSchemaVersion: setup.state.schemaVersion },
          },
    };
  }
  let state: GameStateV4 = setup.state;
  const events: GameEvent[] = [...setup.events];
  const stateHashes = [setup.initialStateHash];
  const mismatch = setupMismatchFailure(replay, state, setup.initialStateHash);
  if (mismatch !== null) {
    return { ok: false, state, events, stateHashes, failedActionIndex: -1, failure: mismatch };
  }
  for (let index = 0; index < replay.actions.length; index += 1) {
    const action = replay.actions[index];
    if (action === undefined) throw new Error("Replay action index unexpectedly missing.");
    const result = applyAction(state, action, context);
    if (!result.ok) {
      return {
        ok: false,
        state,
        events,
        stateHashes,
        failedActionIndex: index,
        failure: result.failure,
      };
    }
    if (result.state.schemaVersion !== 4) {
      throw new Error("Replay V4 action unexpectedly changed the game-state schema version.");
    }
    state = result.state;
    events.push(...result.events);
    stateHashes.push(getStateHash(state));
  }
  return { ok: true, state, events, stateHashes };
}

function replayVersionFive(replay: ReplayDefinitionV5, context: EngineContext): ReplayV5Result {
  if (
    replay.rulesVersion !== context.rulesVersion ||
    replay.cardDataVersion !== context.cardDataVersion
  ) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "CONTEXT_VERSION_MISMATCH",
        message: "Replay versions must match the supplied engine context.",
        path: "rulesVersion",
        details: {
          replayRulesVersion: replay.rulesVersion,
          contextRulesVersion: context.rulesVersion,
        },
      },
    };
  }
  if (
    replay.setup.rulesVersion !== replay.rulesVersion ||
    replay.setup.cardDataVersion !== replay.cardDataVersion ||
    replay.setup.catalog.schemaVersion !== 4
  ) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: {
        code: "REPLAY_SETUP_MISMATCH",
        message: "Replay V5 setup and schema-4 catalog must match the replay header.",
        path: "setup",
        details: { setupCatalogSchemaVersion: replay.setup.catalog.schemaVersion },
      },
    };
  }
  const setup = createMatch(replay.setup);
  if (!setup.ok || setup.state.schemaVersion !== 5) {
    return {
      ok: false,
      state: null,
      events: [],
      stateHashes: [],
      failedActionIndex: -1,
      failure: !setup.ok
        ? setup.failure
        : {
            code: "REPLAY_SETUP_MISMATCH",
            message: "Replay V5 must recreate a schema-V5 game state.",
            path: "setup",
            details: { actualStateSchemaVersion: setup.state.schemaVersion },
          },
    };
  }
  let state: GameStateV5 = setup.state;
  const events: GameEvent[] = [...setup.events];
  const stateHashes = [setup.initialStateHash];
  const mismatch = setupMismatchFailure(replay, state, setup.initialStateHash);
  if (mismatch !== null) {
    return { ok: false, state, events, stateHashes, failedActionIndex: -1, failure: mismatch };
  }
  for (let index = 0; index < replay.actions.length; index += 1) {
    const action = replay.actions[index];
    if (action === undefined) throw new Error("Replay action index unexpectedly missing.");
    const result = applyAction(state, action, context);
    if (!result.ok) {
      return {
        ok: false,
        state,
        events,
        stateHashes,
        failedActionIndex: index,
        failure: result.failure,
      };
    }
    if (result.state.schemaVersion !== 5) {
      throw new Error("Replay V5 action unexpectedly changed the game-state schema version.");
    }
    state = result.state;
    events.push(...result.events);
    stateHashes.push(getStateHash(state));
  }
  return { ok: true, state, events, stateHashes };
}

export function replayActions(replay: ReplayDefinition, context: EngineContext): ReplayResult;
export function replayActions(replay: ReplayDefinitionV2, context: EngineContext): ReplayV2Result;
export function replayActions(replay: ReplayDefinitionV3, context: EngineContext): ReplayV3Result;
export function replayActions(replay: ReplayDefinitionV4, context: EngineContext): ReplayV4Result;
export function replayActions(replay: ReplayDefinitionV5, context: EngineContext): ReplayV5Result;
export function replayActions(
  replay: AnyReplayDefinition,
  context: EngineContext,
): ReplayResult | ReplayV2Result | ReplayV3Result | ReplayV4Result | ReplayV5Result;
export function replayActions(
  replay: AnyReplayDefinition,
  context: EngineContext,
): ReplayResult | ReplayV2Result | ReplayV3Result | ReplayV4Result | ReplayV5Result {
  if (replay.replayVersion === 1) return replayVersionOne(replay, context);
  if (replay.replayVersion === 2) return replayVersionTwo(replay, context);
  if (replay.replayVersion === 3) return replayVersionThree(replay, context);
  if (replay.replayVersion === 4) return replayVersionFour(replay, context);
  return replayVersionFive(replay, context);
}
